import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDownIcon } from 'lucide-react';
import { Eyebrow } from './Eyebrow';
interface Outcome {
  value: string;
  label: string;
}
interface CaseStudy {
  id: string;
  logoUrl: string;
  logoAlt: string;
  logoClassName: string;
  clientName: string;
  companyName: string;
  industry: string;
  headline: React.ReactNode;
  introduction: string;
  storyTitle: string;
  storySummary: string;
  wistiaEmbedUrl: string;
  narrative: React.ReactNode[];
  outcomes?: Outcome[];
}
const CASE_STUDIES: CaseStudy[] = [{
  id: 'andy-benedek',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/rD9S2nSMXRWjfvs4psH55T/BlueCactus_Power_Washing_Logo_White.svg',
  logoAlt: 'Blue Cactus Power Washing',
  logoClassName: 'h-14 max-w-[240px]',
  clientName: 'Andy Benedek',
  companyName: 'Blue Cactus Power Washing',
  industry: 'Exterior cleaning',
  headline: <>Building the system behind the service.</>,
  introduction: 'Andy Benedek shares what it takes to turn a growing exterior-cleaning company into a business built to keep moving.',
  storyTitle: 'More spend was never the answer.',
  storySummary: 'The problem was not a lack of effort. It was a lack of one system that could show what was actually working.',
  wistiaEmbedUrl: 'https://fast.wistia.net/embed/iframe/1zzwnk0ufx',
  outcomes: [{
    value: '2x',
    label: 'more leads a month'
  }, {
    value: '11.5',
    label: 'hours saved in manual data entry weekly'
  }, {
    value: '82%',
    label: 'less agency fees a month'
  }],
  narrative: [<>Before LetLois, Andy was paying more than <span className="font-semibold text-white" data-id="element-20" data-mp="ov93shdefc6d">$4,000 every month</span> across multiple agencies. Each was running its own platform, reporting its own version of the story, and leaving no shared view of what was bringing in qualified work. Money was going into Meta campaigns, Google Ads had not been part of the mix, and the leads arriving were not the kind his team needed.</>, <>The first change was not another campaign. It was consolidation. After stepping away from those agencies, Blue Cactus brought its marketing and follow-up into one LetLois operating system, with Housecall Pro connected to the same workflow. That gave the team one place to see leads, campaigns, and the path each opportunity took after it came in.</>, <>With Meta and Google working from the same foundation, attribution stopped being a guess. The question shifted from <span className="italic text-white" data-id="element-21" data-mp="1om32wa2pjyw3">“Where did this lead come from?”</span> to <span className="font-semibold text-white" data-id="element-22" data-mp="1omcxyc2m0w8n">“Which campaigns are creating work we actually want more of?”</span> It created a cleaner handoff for the team, a clearer view of the pipeline, and a practical system for making marketing decisions.</>, <>That foundation is now supporting the next chapter: expanding Blue Cactus into multiple markets and preparing for a new office. Press play above to hear Andy describe what changed when the tools, campaigns, and follow-up finally started pulling in the same direction.</>]
}, {
  id: 'mike-yandre',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/krFYhZvLhvdUsRKq4jNYGe/Screenshot_2026-07-23_at_1.09.16_PM.png',
  logoAlt: 'B9',
  logoClassName: 'h-20 max-w-[160px] bg-white p-2',
  clientName: 'Mike Yandre',
  companyName: 'B9',
  industry: 'Home services',
  headline: <>A new path to<br data-id="element-23" data-mp="fe9tpcgs1sc5" />finding demand.</>,
  introduction: 'Mike Yandre shares how B9 moved beyond word-of-mouth with attributable Google demand and one place to manage every lead.',
  storyTitle: 'Word-of-mouth was not a growth plan.',
  storySummary: 'The business needed a dependable way to create demand, see which ads were winning, and respond while the lead was still ready to talk.',
  wistiaEmbedUrl: 'https://fast.wistia.net/embed/iframe/bj17lgebff',
  outcomes: [{
    value: '47x',
    label: 'leads a month'
  }, {
    value: '30',
    label: 'new clients in the first 30 days'
  }, {
    value: '6x',
    label: 'ROAS'
  }],
  narrative: [<>Mike came to LetLois ready to grow B9, but without a clear path beyond traditional word-of-mouth. A marketing agency was already involved, yet there was no direct working relationship Mike could rely on - and no simple way to connect marketing activity to real incoming opportunities.</>, <>We started by building the Google Ads foundation: setting up the profile, connecting the pieces, and giving the business a new demand channel without requiring an outsized budget. With an investment of a few thousand dollars per month, the campaigns began bringing in enough interest that the team needed a better way to keep up with every conversation.</>, <>That is where the system mattered as much as the ads. LetLois connected attribution back to the platform so Mike could see which ads were producing leads worth funding. Instead of guessing where to put the next dollar, the team could follow the activity, shift spend toward what was winning, and keep the process moving.</>, <>We also brought lead response into one shared workspace. Mike and his business partner could see new opportunities, text prospects, and make calls from the same platform - rather than trying to coordinate follow-up from personal iPhones. Press play above to hear Mike explain what it felt like to finally have a channel that could bring the right conversations to the business.</>]
}, {
  id: 'hanson-hanger',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/mohTfnLekR6H4e8UAzc1Ej/hanse-new-logo_redline_pool_tile-200.png',
  logoAlt: 'Red Line Pool Tile Restoration',
  logoClassName: 'h-20 max-w-[160px] rounded-full bg-white',
  clientName: 'Hanson Hanger',
  companyName: 'Red Line Pool Tile Cleaning',
  industry: 'Pool tile cleaning',
  headline: <>From message chaos<br data-id="element-24" data-mp="25x8m2bmnxc57" />to a working pipeline.</>,
  introduction: 'Hanson Hanger shares how Red Line Pool Tile Cleaning turned scattered Meta conversations into a faster, more trackable path to booked work.',
  storyTitle: 'The leads were there. The system was not.',
  storySummary: 'Meta was generating interest, but every inquiry created a manual chase across Instagram, Facebook Messenger, and personal phones.',
  wistiaEmbedUrl: 'https://fast.wistia.net/embed/iframe/nzf1ft51ge',
  outcomes: [{
    value: '41',
    label: 'new website leads'
  }, {
    value: '3x',
    label: 'ROAS'
  }, {
    value: '15',
    label: 'hours saved a week collecting contact data'
  }],
  narrative: [<>When we met Hanson, Red Line Pool Tile Cleaning was starting to see traction through Meta. The issue was not a lack of messages. The issue was converting those messages into work. Instagram DMs and Facebook Messenger conversations were arriving separately, and each one required Hanson to ask for a phone number and email before switching to his iPhone to call and try to book the job.</>, <>That fragmented follow-up created unnecessary friction for both the prospect and the team. We consolidated the inbound flow into one unified LetLois workspace and configured forms to capture the data that matters at the start: name, phone number, email, and the details needed to move the opportunity forward.</>, <>With the essentials captured in one place, Hanson could text and respond to leads from the same platform, whether the conversation started on Meta or elsewhere. We also reworked the website around conversion, creating a clearer path for people to inquire and giving the business a more dependable owned channel alongside its paid social activity.</>, <>The impact was immediate. In the first 30 days, Red Line saw <span className="font-semibold text-white" data-id="element-25" data-mp="30z9zx35yewqv">more than 1,100 messages</span> and <span className="font-semibold text-white" data-id="element-26" data-mp="28rolzve25sf">41 brand-new website leads</span> from the new site. Press play above to hear Hanson explain how replacing the daily follow-up chaos changed the way his team could handle demand.</>]
}, {
  id: 'nathan-rance',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/md9g2P5GKfrsgFdymDAYJz/RANCE_(1).png',
  logoAlt: 'Rance Construction',
  logoClassName: 'h-20 max-w-[160px] object-contain object-left',
  clientName: 'Nathan Rance',
  companyName: 'Rance Construction',
  industry: 'Construction',
  headline: <>From referrals<br data-id="element-27" data-mp="2wyf08e04z4cz" />to a real system.</>,
  introduction: 'Nathan Rance shares how Rance Construction built a professional digital presence and a unified operating system for the next stage of growth.',
  storyTitle: 'Referrals built the business. A system made it scalable.',
  storySummary: 'A professional website and one connected workspace gave the team a clearer path to grow without relying on word of mouth alone.',
  wistiaEmbedUrl: 'https://fast.wistia.net/embed/iframe/qyid50y558',
  outcomes: [{
    value: '3x',
    label: 'leads a month'
  }, {
    value: '10x',
    label: 'website traffic'
  }, {
    value: '3',
    label: 'net new organic leads in the first 60 days'
  }],
  narrative: [<>Nathan came to us having never used a website or traditional marketing to grow Rance Construction. The business was built the way many strong trade businesses start: he sourced jobs, did great work, and trusted referrals to bring the next opportunity through the door.</>, <>That approach created a loyal base, but it made growth hard to control. Without a professional online presence, there was no clear place for new prospects to learn about the business, understand its work, or move from first impression to a real conversation.</>, <>We built the website and set Rance Construction up with the tools needed to support a more professional operation. The business had a stronger destination for traffic and a clearer way to turn interest into leads, creating momentum beyond the referral network.</>, <>We also brought the backend into one unified system so the team could see who was texting whom, which client was being supported, and where each conversation stood. With the website, marketing, and follow-up working together, Rance Construction had a foundation built to scale. Press play above to hear Nathan describe what changed.</>]
}, {
  id: 'donovan-smith',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/aqRLjeEoJfavPximVtv5ua/ChatGPT_Image_Jul_23,_2026,_01_02_31_PM.png',
  logoAlt: 'Smith Home Builders',
  logoClassName: 'h-28 max-w-[260px] object-contain',
  clientName: 'Donovan Smith',
  companyName: 'Smith Home Builders',
  industry: 'New home construction',
  headline: <>A website built<br data-id="element-28" data-mp="reznzhys20n0" />to earn trust.</>,
  introduction: 'Donovan Smith shares why Smith Home Builders needed a professional home online before word-of-mouth growth could become a stronger, more dependable presence.',
  storyTitle: 'No website meant no place to build confidence.',
  storySummary: 'For a residential builder, the first impression has to establish credibility before a prospect ever decides to start a conversation.',
  wistiaEmbedUrl: 'https://fast.wistia.net/embed/iframe/w1xf3l3fmr',
  outcomes: [{
    value: '15',
    label: 'new website leads in the first 30 days'
  }, {
    value: '2x',
    label: 'ROAS'
  }, {
    value: '2',
    label: 'closed deals in the first 90 days'
  }],
  narrative: [<>Donovan came to LetLois in need of a website. Smith Home Builders was growing through word of mouth, but without a professional online presence there was no central place to send prospective clients, no portfolio that could work around the clock, and no strong bridge between an introduction and the trust needed to start a residential construction conversation.</>, <>That matters in new residential construction, where clients are evaluating more than a finished product. They are deciding who will manage a major project, communicate clearly, and bring their vision to life. A well-built online presence gives that decision a foundation before the first call ever happens.</>, <>We created a site designed to present Smith Home Builders with the same level of care expected in the work itself: a clear home for the brand, an accessible portfolio, and a professional destination for traffic generated through referrals and future marketing.</>, <>The result is a place where Donovan can direct new prospects with confidence - one that helps close the trust gap, shows the quality of the work, and gives every word-of-mouth introduction somewhere meaningful to go. Press play above to hear Donovan explain what that shift means for the business.</>]
}, {
  id: 'xander-winther',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/4FVuNYaBL3RRVnRmuxbSuB/tmp7r03_gc_.webp',
  logoAlt: 'Winther Wealth - Create Abundance',
  logoClassName: 'h-16 max-w-[300px] object-contain object-left',
  clientName: 'Xander Winther',
  companyName: 'Winther Wealth',
  industry: 'Wealth management',
  headline: <>Building demand<br data-id="element-29" data-mp="2sjvxjzizij6" />with momentum.</>,
  introduction: 'Xander Winther shares how a first Meta campaign helped turn a new wealth-management practice into a more predictable path for larger, faster-moving deals.',
  storyTitle: 'The right campaign changes the kind of client you can reach.',
  storySummary: 'Launching demand was only the beginning. The goal was to create a system that brought in stronger opportunities and gave the business room to grow.',
  wistiaEmbedUrl: 'https://fast.wistia.net/embed/iframe/063ngtuo0o',
  outcomes: [{
    value: '7x',
    label: 'deal size'
  }, {
    value: '50%',
    label: 'reduction of time to close'
  }, {
    value: '3.6x',
    label: 'ROAS'
  }],
  narrative: [<>When we first met Xander, Winther Wealth was a new business working to bring clients through the door. The value was there, but the practice needed a clearer way to reach the right people and turn early interest into real conversations.</>, <>We helped build the ad creative and launch Xander’s first Meta campaign. The early results did more than generate visibility: the campaign created momentum around offers that were helping the business move deals forward faster and connect with prospects ready for a higher-value conversation.</>, <>That shift changed the quality of the pipeline. Return on ad spend increased, deal size rose, and the path from interest to close became more efficient. Instead of relying on disconnected activity, the business had a repeatable demand channel it could learn from and improve.</>, <>We are also setting Xander up inside LetLois, giving the practice one place to organize every incoming lead and build a streamlined operating system around follow-up. Press play above to hear Xander describe what it means to create a growth engine from the ground up.</>]
}, {
  id: 'daniel-robertson',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/qw5jf3jXVJiFSdCfMagz5n/Cloverfield-olive.png',
  logoAlt: 'Cloverfield Coaching',
  logoClassName: 'h-20 max-w-[300px] object-contain object-left',
  clientName: 'Daniel Robertson',
  companyName: 'Cloverfield Coaching',
  industry: 'Coaching',
  headline: <>A website that<br data-id="element-30" data-mp="29xzsm8knknga" />made trust tangible.</>,
  introduction: 'Daniel Robertson shares how Cloverfield Coaching turned an early online presence into a stronger foundation for leads, marketing, and client confidence.',
  storyTitle: 'The work was strong. The online presence had to catch up.',
  storySummary: 'A clear, credible website gave prospective clients a better way to understand the business before deciding to reach out.',
  wistiaEmbedUrl: 'https://fast.wistia.net/embed/iframe/em4qcrbmhb',
  outcomes: [{
    value: '25',
    label: 'new website leads in the first 30 days'
  }, {
    value: '4.3x',
    label: 'ROAS'
  }, {
    value: '1',
    label: 'unified platform vs 3'
  }],
  narrative: [<>Daniel was one of our first clients. When we met, Cloverfield Coaching was working to strengthen its online presence, generate new leads, and give prospective clients a clearer understanding of the services the business provided.</>, <>We started with the website. Once it was live, Daniel immediately had something he was excited to share - a professional home for the business that explained the offer, showed the quality of the work, and gave people a more confident first impression.</>, <>That new foundation also lifted the rest of the marketing. SEO, paid campaigns, and referral conversations all had a stronger destination, helping prospects understand the services more quickly and improving the trust factor before the first conversation.</>, <>We also brought the operating side into one unified LetLois platform, replacing three disconnected tools with a streamlined place to manage leads and keep follow-up moving. Press play above to hear Daniel describe what that clearer system changed for Cloverfield Coaching.</>]
}];
export function FeaturedCaseStudy() {
  return <section className="w-full bg-neutral-950 text-white" data-id="element-31" data-mp="2ck8gdux8jol7">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28" data-id="element-32" data-mp="2hit8t868x3z3">
        {CASE_STUDIES.map((caseStudy, index) => <CaseStudyFeature key={caseStudy.id} caseStudy={caseStudy} isFirst={index === 0} data-id="element-33" data-mp="22t43d0r4edt0" data-mpi={index} />)}
      </div>
    </section>;
}
interface CaseStudyFeatureProps {
  caseStudy: CaseStudy;
  isFirst: boolean;
}
function CaseStudyFeature({
  caseStudy,
  isFirst
}: CaseStudyFeatureProps) {
  return <article className="mb-24 border-b border-white/10 pb-20 last:mb-0 last:border-b-0 last:pb-0 lg:mb-32 lg:pb-28" data-id="element-34" data-mp="1jusycl75kvfs">
      {caseStudy.outcomes && <motion.section initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.45
    }} aria-label="Case study outcomes" className="mb-16 py-10 lg:mb-20 lg:py-14" data-id="element-35" data-mp="1oue71nc4ki5p">
          <div className="grid gap-8 sm:grid-cols-3 sm:gap-5" data-id="element-36" data-mp="26xqqgj7hfosf">
            {caseStudy.outcomes.map(outcome => <div key={outcome.label} className="sm:border-r sm:border-white/10 sm:pr-5 last:sm:border-r-0" data-id="element-37" data-mp="221t9qbsb4gf4">
                <div className="font-display text-6xl uppercase leading-none text-white sm:text-7xl lg:text-8xl" data-id="element-38" data-mp="1s9nkvaxcqf45">{outcome.value}</div>
                <p className="mt-3 max-w-[15rem] text-sm leading-relaxed text-white/60" data-id="element-39" data-mp="1nat043ftqghe">{outcome.label}</p>
              </div>)}
          </div>
        </motion.section>}

      <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-20" data-id="element-40" data-mp="1e9csg3ge8sgv">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.45
      }} data-id="element-41" data-mp="19b1i7rm6sfy4">
          <Eyebrow data-id="element-42" data-mp="33oj8hmdz7qwu">{isFirst ? '// Featured case study' : '// Next case study'}</Eyebrow>
          {caseStudy.logoUrl ? <img src={caseStudy.logoUrl} alt={caseStudy.logoAlt} className={`mt-8 w-auto object-contain object-left ${caseStudy.logoClassName}`} data-id="element-43" data-mp="3b7k65r23iqca" /> : <div className="mt-8 font-mono-eyebrow text-xl font-semibold uppercase tracking-[0.12em] text-white" data-id="element-44" data-mp="3kyww2d4c36fo">
              Red Line <span className="text-brand-blue" data-id="element-45" data-mp="3pz2k7jbeaat0">Pool Tile Cleaning</span>
            </div>}
          <h2 className="mt-10 font-display text-5xl uppercase leading-[0.95] sm:text-6xl" data-id="element-46" data-mp="2x8kgcg2uk5ej">
            {caseStudy.headline}
          </h2>
          <p className="mt-7 max-w-xl text-lg leading-relaxed text-white/60" data-id="element-47" data-mp="326lz0zwd22ph">{caseStudy.introduction}</p>

          <dl className="mt-10 grid grid-cols-2 gap-px border border-white/10 bg-white/10 sm:max-w-md" data-id="element-48" data-mp="377rw19x20c4t">
            <div className="bg-neutral-950 p-5" data-id="element-49" data-mp="hgmuz1ifsds5">
              <dt className="font-mono-eyebrow text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45" data-id="element-50" data-mp="mghicv306l8g">Industry</dt>
              <dd className="mt-2 text-sm font-medium text-white" data-id="element-51" data-mp="1wvm4x5l2u394">{caseStudy.industry}</dd>
            </div>
            <div className="bg-neutral-950 p-5" data-id="element-52" data-mp="21svtyk4k0cwf">
              <dt className="font-mono-eyebrow text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45" data-id="element-53" data-mp="21rkqymixn96d">Featured voice</dt>
              <dd className="mt-2 text-sm font-medium text-white" data-id="element-54" data-mp="2amh7k04zfo49">{caseStudy.clientName}</dd>
            </div>
          </dl>

          {isFirst && <a href="#proof" className="mt-10 inline-flex items-center gap-2 font-mono-eyebrow text-xs font-semibold uppercase tracking-[0.15em] text-brand-blue transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue" data-id="element-55" data-mp="1dy1i041vher5">
              See the proof
              <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-56" data-mp="2ycbi2b1tx0lc" />
            </a>}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.45,
        delay: 0.1
      }} className="relative mx-auto w-full max-w-md lg:max-w-none" data-id="element-57" data-mp="339v8e9bsx3tn">
          <div className="absolute -inset-3 border border-brand-blue/35" aria-hidden="true" data-id="element-58" data-mp="33o8j644vxgir" />
          <div className="relative aspect-[9/16] overflow-hidden bg-black" data-id="element-59" data-mp="2zo872q59dhev">
            <iframe src={caseStudy.wistiaEmbedUrl} title={`${caseStudy.clientName} of ${caseStudy.companyName} case study`} className="h-full w-full border-0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-id="element-60" data-mp="2uobz6ftfx5u1" />
          </div>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.45
    }} className="mt-20 lg:mt-28" data-id="element-61" data-mp="2gq5ddy1rmwaq">
        <div className="grid gap-10 lg:grid-cols-[1.32fr_0.68fr] lg:gap-20" data-id="element-62" data-mp="28fww1e4v894h">
          <div className="order-2 max-w-3xl space-y-7 text-lg leading-relaxed text-white/70 lg:order-1" data-id="element-63" data-mp="28dtvi0636xli">
            {caseStudy.narrative.map((paragraph, index) => <p key={index} data-id="element-64" data-mp="1zgjk8et9r8gl" data-mpi={index}>{paragraph}</p>)}
          </div>

          <div className="order-1 lg:order-2 lg:pt-1" data-id="element-65" data-mp="23gbxh4tly2sa">
            <Eyebrow data-id="element-66" data-mp="23er25czgyftd">// The story</Eyebrow>
            <h3 className="mt-5 font-display text-4xl uppercase leading-[0.98] sm:text-5xl" data-id="element-67" data-mp="2r5wirud8hc4q">{caseStudy.storyTitle}</h3>
            <p className="mt-6 max-w-sm text-base leading-relaxed text-white/55" data-id="element-68" data-mp="2w4g4purtunx3">{caseStudy.storySummary}</p>
          </div>
        </div>
      </motion.div>
    </article>;
}